(() => {
  const dialog = document.getElementById("sma-free-entry-flow");
  if (!dialog) return;

  const forms = Object.fromEntries(
    [1, 2, 3].map((step) => [
      step,
      dialog.querySelector(`[data-free-entry-step="${step}"]`),
    ]),
  );
  const email = dialog.querySelector("#sma-free-entry-email");
  const firstName = dialog.querySelector("#sma-free-entry-first-name");
  const lastName = dialog.querySelector("#sma-free-entry-last-name");
  const phone = dialog.querySelector("#sma-free-entry-phone");
  const consent = dialog.querySelector("#sma-free-entry-consent");
  const emailMarketing = dialog.querySelector(
    "#sma-free-entry-email-marketing",
  );
  const smsMarketing = dialog.querySelector("#sma-free-entry-sms-marketing");
  const optinStatus = dialog.querySelector("[data-free-entry-optin-status]");
  const code = dialog.querySelector("#sma-free-entry-code");
  const resendButton = dialog.querySelector("[data-free-entry-resend]");
  const status = dialog.querySelector("[data-free-entry-status]");
  let verificationToken = null;
  let verifiedPhone = null;
  let resendTimer = null;
  let requestPending = false;

  const showError = (step, message) => {
    const element = dialog.querySelector(`[data-free-entry-error="${step}"]`);
    element.textContent = message;
    element.hidden = !message;
  };

  const clearMessages = () => {
    [1, 2, 3].forEach((step) => showError(step, ""));
    status.textContent = "";
    status.hidden = true;
    dialog
      .querySelectorAll("input[aria-invalid]")
      .forEach((input) => input.removeAttribute("aria-invalid"));
  };

  const showStep = (step) => {
    clearMessages();
    dialog.querySelectorAll("[data-free-entry-step]").forEach((element) => {
      element.hidden = element.dataset.freeEntryStep !== String(step);
    });
    dialog.querySelectorAll("[data-free-entry-progress]").forEach((element) => {
      element.toggleAttribute(
        "data-complete",
        Number(element.dataset.freeEntryProgress) < step,
      );
      if (element.dataset.freeEntryProgress === String(step)) {
        element.setAttribute("aria-current", "step");
      } else {
        element.removeAttribute("aria-current");
      }
    });
    if (step === 4) {
      dialog
        .querySelectorAll("[data-free-entry-progress]")
        .forEach((element) => element.removeAttribute("aria-current"));
    }
    const focusTarget =
      step === 1
        ? email
        : step === 2
          ? firstName
          : step === 3
            ? code
            : dialog.querySelector('[data-free-entry-step="4"] button');
    focusTarget.focus();
  };

  const setPending = (form, pending) => {
    requestPending = pending;
    form
      .querySelectorAll("button:not([data-free-entry-resend]), input")
      .forEach((element) => {
        element.disabled = pending;
      });
    resendButton.disabled = pending || Boolean(resendTimer);
  };

  const normalizePhone = (value) => {
    let digits = value.replace(/\D/g, "");
    if (digits.length === 11 && digits.startsWith("1"))
      digits = digits.slice(1);
    return /^[2-9]\d{2}[2-9]\d{6}$/.test(digits) ? `+1${digits}` : null;
  };

  const requestGraphQL = async (query) => {
    const response = await fetch(dialog.dataset.graphqlUrl, {
      method: "POST",
      credentials: "same-origin",
      cache: "no-store",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ query }),
    });
    let result;
    try {
      result = await response.json();
    } catch {
      throw new Error("");
    }
    if (!response.ok || !result || result.errors?.length) {
      const first = result?.errors?.[0];
      const error = new Error(
        typeof first?.message === "string" ? first.message : "",
      );
      error.code = first?.extensions?.code;
      throw error;
    }
    return result.data;
  };

  // Server errors are logged for us; people see a short, plain message instead of the raw text.
  const friendlyError = (error, stage) => {
    console.error(`Free-entry ${stage} failed:`, error);
    const raw = `${error?.code || ""} ${error?.message || ""}`;
    if (/already|exist|registered|taken|duplicate/i.test(raw))
      return "It looks like you already have an account. Log in to see your entries.";
    if (stage === "send") {
      if (/BAD_USER_INPUT|phone/i.test(raw))
        return "That number doesn't look right. Please enter a 10-digit U.S. mobile number.";
      return "We couldn't send your code right now. Please try again in a minute.";
    }
    if (/UNAUTHENTICATED|invalid|expired|code/i.test(raw))
      return "That code didn't work. Please check it and try again, or tap Resend code.";
    return "We couldn't finish setting up your account. Please try again in a minute.";
  };

  const sendMarketingOptIn = (accountEmail, accountPhone, channels, accessToken) =>
    window.SMAFreeEntryMarketing.subscribe({
      email: accountEmail,
      phone: accountPhone,
      firstName: firstName.value.trim(),
      lastName: lastName.value.trim(),
      channels,
      ...(window.SMA_MARKETING?.proxyEnabled ? {
        accessToken,
        consent: {
          version: window.SMA_MARKETING.consentVersion,
          ...(channels.includes("email") ? { email: dialog.querySelector("#sma-free-entry-email-marketing-text").textContent.trim() } : {}),
          ...(channels.includes("sms") ? { sms: dialog.querySelector("#sma-free-entry-sms-marketing-full").textContent.trim() } : {}),
        },
      } : {}),
    });

  // pfm-track sends this as GET {endpoint}/pb?clickId={clickId}&type=AccountCreation
  const trackRegistration = () => {
    try {
      if (window.pfm?.track) window.pfm.track("CompleteRegistration");
      else
        (window.pfmQueue = window.pfmQueue || []).push([
          "track",
          "CompleteRegistration",
        ]);
    } catch (error) {
      console.error("Free-entry registration tracking failed:", error);
    }
  };

  const startVerification = async (resend = false) => {
    const normalizedPhone = normalizePhone(phone.value);
    if (!normalizedPhone) {
      phone.setAttribute("aria-invalid", "true");
      showError(2, "Enter a valid 10-digit U.S. mobile number.");
      phone.focus();
      return;
    }
    const currentForm = resend ? forms[3] : forms[2];
    clearMessages();
    setPending(currentForm, true);
    try {
      const data = await requestGraphQL(
        `mutation { userPhoneVerifyBegin(dto: { phone: ${JSON.stringify(normalizedPhone)} }) { token } }`,
      );
      const token = data?.userPhoneVerifyBegin?.token;
      if (typeof token !== "string" || !token) {
        throw new Error("No verification token returned.");
      }
      verificationToken = token;
      verifiedPhone = normalizedPhone;
      dialog.querySelector("[data-free-entry-phone-display]").textContent =
        `•••-•••-${normalizedPhone.slice(-4)}`;
      if (resend) {
        status.textContent = "A new code is on its way.";
        status.hidden = false;
        code.focus();
      } else {
        code.value = "";
        showStep(3);
      }
      startResendCooldown(30);
    } catch (error) {
      showError(resend ? 3 : 2, friendlyError(error, "send"));
    } finally {
      setPending(currentForm, false);
    }
  };

  function startResendCooldown(seconds) {
    if (resendTimer) clearInterval(resendTimer);
    let remaining = seconds;
    resendButton.disabled = true;
    resendButton.textContent = `Resend code in ${remaining}s`;
    resendTimer = setInterval(() => {
      remaining -= 1;
      if (remaining <= 0) {
        clearInterval(resendTimer);
        resendTimer = null;
        resendButton.disabled = false;
        resendButton.textContent = "Resend code";
      } else {
        resendButton.textContent = `Resend code in ${remaining}s`;
      }
    }, 1000);
  }

  forms[1].addEventListener("submit", (event) => {
    event.preventDefault();
    clearMessages();
    email.value = email.value.trim();
    if (!email.reportValidity()) return;
    showStep(2);
  });

  forms[2].addEventListener("submit", (event) => {
    event.preventDefault();
    clearMessages();
    firstName.value = firstName.value.trim();
    lastName.value = lastName.value.trim();
    if (!forms[2].reportValidity()) return;
    startVerification();
  });

  forms[3].addEventListener("submit", async (event) => {
    event.preventDefault();
    if (requestPending) return;
    clearMessages();
    code.value = code.value.trim();
    if (!forms[3].reportValidity()) return;
    if (!verificationToken) {
      showError(3, "Please request a new code.");
      return;
    }
    if (!verifiedPhone) {
      showError(3, "Please request a new code.");
      return;
    }
    const consentText = consent.dataset.consentText?.trim();
    if (!consent.checked || !consentText) {
      showError(
        3,
        "Please confirm your age and agree to the rules, policies, account creation, and SMS verification before continuing.",
      );
      return;
    }
    setPending(forms[3], true);
    try {
      const accountEmail = email.value.trim();
      const accountPhone = verifiedPhone;
      const marketingChannels = [
        ...(emailMarketing.checked ? ["email"] : []),
        ...(smsMarketing.checked ? ["sms"] : []),
      ];
      const consentVersion =
        dialog.dataset.consentVersion?.trim() || "account-sms-v2";
      const consentInput = `text: ${JSON.stringify(consentText)}, version: ${JSON.stringify(consentVersion)}`;
      const data = await requestGraphQL(
        `mutation { userPhoneSignUp(token: ${JSON.stringify(verificationToken)}, code: ${JSON.stringify(code.value)}, email: ${JSON.stringify(accountEmail)}, firstName: ${JSON.stringify(firstName.value.trim())}, lastName: ${JSON.stringify(lastName.value.trim())}, consent: { ${consentInput} }) { accessToken refreshToken } }`,
      );
      const credentials = data?.userPhoneSignUp;
      if (
        typeof credentials?.accessToken !== "string" ||
        !credentials.accessToken ||
        typeof credentials?.refreshToken !== "string" ||
        !credentials.refreshToken
      ) {
        throw new Error("No credentials returned.");
      }
      verificationToken = null;
      verifiedPhone = null;
      let optinFailed = false;
      if (marketingChannels.length) {
        try {
          await sendMarketingOptIn(
            accountEmail,
            accountPhone,
            marketingChannels,
            credentials.accessToken,
          );
        } catch (error) {
          optinFailed = true;
          console.error("Free-entry marketing opt-in failed:", error);
        }
      }
      trackRegistration();
      showStep(4);
      // Only a failure is worth saying here (the success screen already tells them to check their email).
      optinStatus.textContent = optinFailed
        ? "Your account was created, but we couldn't sign you up for emails. Contact us if your code doesn't arrive."
        : "";
      optinStatus.hidden = !optinStatus.textContent;
    } catch (error) {
      code.setAttribute("aria-invalid", "true");
      showError(3, friendlyError(error, "sign-up"));
      code.focus();
    } finally {
      setPending(forms[3], false);
    }
  });

  resendButton.addEventListener("click", () => {
    if (!requestPending && !resendButton.disabled) startVerification(true);
  });

  dialog.querySelectorAll("[data-free-entry-back]").forEach((button) => {
    button.addEventListener("click", () => {
      if (!requestPending) showStep(Number(button.dataset.freeEntryBack));
    });
  });

  dialog.querySelectorAll("[data-free-entry-close]").forEach((button) => {
    button.addEventListener("click", () => dialog.close());
  });
  dialog.addEventListener("click", (event) => {
    if (event.target === dialog) dialog.close();
  });

  const scrim = document.querySelector("[data-free-entry-scrim]");
  const setScrim = (visible) => {
    if (scrim) scrim.hidden = !visible;
  };
  let inerted = [];
  let returnFocus = null;
  const setPageInert = (inert) => {
    if (inert) {
      inerted = [...document.body.children].filter(
        (node) => node !== dialog && node !== scrim && !node.inert,
      );
      inerted.forEach((node) => {
        node.inert = true;
      });
      return;
    }
    inerted.forEach((node) => {
      node.inert = false;
    });
    inerted = [];
  };
  const focusable = () =>
    [
      ...dialog.querySelectorAll(
        "a[href], button:not([disabled]), input:not([disabled]):not([type='hidden']), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])",
      ),
    ].filter(
      (element) =>
        !element.closest("[hidden]") && element.getClientRects().length > 0,
    );
  dialog.addEventListener("close", () => {
    setScrim(false);
    setPageInert(false);
    if (
      returnFocus instanceof HTMLElement &&
      returnFocus.isConnected &&
      returnFocus !== document.body
    ) {
      returnFocus.focus();
    }
    returnFocus = null;
  });
  scrim?.addEventListener("click", () => {
    if (dialog.open) dialog.close();
  });
  dialog.addEventListener("keydown", (event) => {
    if (event.key !== "Tab" || !dialog.open) return;
    const items = focusable();
    if (!items.length) {
      event.preventDefault();
      dialog.focus();
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && (active === first || !dialog.contains(active))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (active === last || !dialog.contains(active))) {
      event.preventDefault();
      first.focus();
    }
  });
  document.addEventListener("focusin", (event) => {
    if (!dialog.open || dialog.contains(event.target)) return;
    const items = focusable();
    (items[0] || dialog).focus();
  });
  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape" || !dialog.open) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    dialog.close();
  });

  window.addEventListener("sma:open-free-entry", () => {
    if (!dialog.open) {
      returnFocus =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;
      setScrim(true);
      setPageInert(true);
      dialog.show();
      showStep(verificationToken ? 3 : 1);
    }
  });
})();
